import { MapsAPILoader } from '@agm/core';
import { Location } from '@angular-material-extensions/google-maps-autocomplete';
import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ScGoogleMapSearchConfig } from '../../../../models/generic-form.model';
import PlaceResult = google.maps.places.PlaceResult;

// @dynamic
@Component({
  selector: 'sc-google-map-search',
  templateUrl: './google-map-search.component.html',
  styleUrls: ['./google-map-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GoogleMapSearchComponent),
      multi: true
    }
  ]
})
export class GoogleMapSearchComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() public config: ScGoogleMapSearchConfig = new ScGoogleMapSearchConfig({});
  @Input() public disabled: boolean;
  @Input() placeholder: string;
  @Output() public valueChange: EventEmitter<string | any> = new EventEmitter();

  public address$: Observable<string>;
  public zoom: number;
  public latitude: number;
  public longitude: number;
  public selectedAddress: PlaceResult;
  private _geoCoder: any;
  private _geoCoderInitialized: boolean;

  private _address: Subject<string> = new Subject();

  constructor(private _mapsAPILoader: MapsAPILoader, @Inject(DOCUMENT) private _document: Document) {}
  @Input() public set value(value: string) {
    this._getAddress(value);
  }

  onChange = (checked: string | any) => {};
  onTouched = () => {};

  ngOnInit() {
    this.address$ = this._address.asObservable().pipe(tap(() => window.dispatchEvent(new Event('resize'))));
    this.zoom = 14;
  }

  ngOnDestroy() {
    this._destroyPacContainer();
  }

  writeValue(value: string) {
    if (typeof value === 'string') {
      this._getAddress(value);
    }

    if (this.config.type === 'coords') {
      this.onChange(value);
    }
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onLocationSelected(location: Location) {
    this.latitude = location.latitude;
    this.longitude = location.longitude;

    if (!this.disabled) {
      const value = `${location.latitude}, ${location.longitude}`;

      if (this.config.type === 'coords') {
        this.valueChange.emit(value);
        this.writeValue(value);
      } else {
        this._getAddress(value);
      }
    }
  }

  markerDragEnd({ coords }) {
    this.latitude = coords.lat;
    this.longitude = coords.lng;
    if (!this.disabled) {
      const value = `${coords.lat},${coords.lng}`;
      if (this.config.type === 'coords') {
        this.valueChange.emit(value);
        this.writeValue(value);
      } else {
        this._getAddress(value);
      }
    }
  }

  private _destroyPacContainer() {
    try {
      // have to remove manually
      const pacContainer = this._document.querySelector('.pac-container') as HTMLElement;
      this._document.body.removeChild(pacContainer);
    } catch (error) {}
  }

  private async _initializeGeocoder() {
    return this._mapsAPILoader.load().then(() => {
      this._geoCoderInitialized = true;
      return new google.maps.Geocoder();
    });
  }

  private async _getAddress(value: string) {
    if (!value) {
      console.warn('No coords found!');
      return;
    }
    const [lat, lng] = value.split(',');
    const latitude = Number(lat.trim());
    const longitude = Number(lng.trim());

    if (isNaN(latitude) || isNaN(longitude)) {
      throw new Error('Latitude and longitude is not the correct format! Example: "40.54, 20.56"');
    }

    this.latitude = latitude;
    this.longitude = longitude;

    if (!this._geoCoderInitialized) {
      this._geoCoder = await this._initializeGeocoder();
    }

    this._geoCoder.geocode({ location: { lat: latitude, lng: longitude } }, (results: PlaceResult[], status: string) => {
      if (status === 'OK') {
        if (results[0]) {
          const [place]: PlaceResult[] = results;
          if (this.config.type === 'fulladdress') {
            const address = place.address_components;
            const streetNumber = address.find(item => item.types.some(type => type === 'street_number'));
            const streetName = address.find(item => item.types.some(type => type === 'route'));
            const cityName = address.find(item => item.types.some(type => type === 'locality'));

            const country = address.find(item => item.types.some(type => type === 'country'));

            const fullAddress = {
              country: country ? country.short_name : '',
              cityName: cityName ? cityName.short_name : '',
              streetNum: streetNumber ? streetNumber.short_name : '',
              streetName: streetName ? streetName.short_name : '',
              formattedAddress: place.formatted_address ? place.formatted_address : ''
            };
            this.valueChange.emit(fullAddress);
            this.onChange(fullAddress);
          }

          this._address.next(place.formatted_address);
        }
      }
    });
  }
}
